import React, { useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import Reveal from "../Reveal/Reveal";
import {
  IconShieldCheck,
  IconCertificate,
  IconGlobeLeaf,
  IconTransfer,
  IconReport,
  IconLeafRegs,
  IconArrowRight,
} from "../Icons/Icons";
import "../../assets/css/services.css";

const SERVICES = [
  {
    icon: IconShieldCheck,
    title: "Ofgem Compliance",
    text: "ROC/REGO and ND-RHI/GGSS submissions, problem-case resolution and audit support.",
  },
  {
    icon: IconCertificate,
    title: "Green Gas Certificate Scheme",
    text: "GGCS submissions and ongoing scheme administration, quarter after quarter.",
  },
  {
    id: "iscc-compliance",
    icon: IconGlobeLeaf,
    title: "ISCC Compliance",
    text: "Training, mass balance, PoS support, GHG calculations and internal audits.",
  },
  {
    icon: IconTransfer,
    title: "Nabisy Transfers",
    text: "Management and administration of Nabisy transfers on your behalf.",
  },
  {
    icon: IconReport,
    title: "SECR Reports",
    text: "Streamlined Energy & Carbon Reporting from data gathering to submission.",
  },
  {
    icon: IconLeafRegs,
    title: "Sustainability & Net Zero",
    text: "Support across UK ETS, the SAF Mandate and DfT credits.",
  },
];

function Services() {
  const trackRef = useRef(null);
  const pausedRef = useRef(false);

  const scrollByCard = (direction) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.querySelector(".service-card");
    const step = card ? card.offsetWidth + 20 : 320;
    track.scrollBy({ left: direction * step, behavior: "smooth" });
  };

  // Auto-scroll – advances one card at a time, loops back at the end,
  // pauses on hover/touch and respects reduced-motion preferences.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;

    const reduce = window.matchMedia
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false;
    if (reduce) return undefined;

    const id = setInterval(() => {
      if (pausedRef.current) return;
      const t = trackRef.current;
      if (!t) return;
      const maxScroll = t.scrollWidth - t.clientWidth;
      if (t.scrollLeft >= maxScroll - 4) {
        t.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        const card = t.querySelector(".service-card");
        const step = card ? card.offsetWidth + 20 : 320;
        t.scrollBy({ left: step, behavior: "smooth" });
      }
    }, 3500);

    return () => clearInterval(id);
  }, []);

  const pause = () => {
    pausedRef.current = true;
  };
  const resume = () => {
    pausedRef.current = false;
  };

  return (
    <section className="section section-tint" id="services">
      <div className="container">
        <div className="section-header">
          <Reveal direction="up">
            <span className="section-label">What We Do</span>
            <h2 className="section-title">Our Services</h2>
            <p className="section-subtitle">
              Specialist support across the certification schemes and regulatory
              frameworks that matter most to renewable energy businesses.
            </p>
          </Reveal>
        </div>

        <div className="services-viewport">
          <button
            type="button"
            className="services-arrow services-arrow--left"
            aria-label="Previous services"
            onClick={() => scrollByCard(-1)}
          >
            <IconArrowRight
              width={20}
              height={20}
              className="services-arrow-prev"
            />
          </button>

          <div
            className="services-carousel"
            ref={trackRef}
            onMouseEnter={pause}
            onMouseLeave={resume}
            onTouchStart={pause}
            onTouchEnd={resume}
          >
            {SERVICES.map((service) => {
              const Icon = service.icon;
              return (
                <article className="service-card" key={service.title}>
                  <span className="service-icon">
                    <Icon width={24} height={24} />
                  </span>
                  <h3 className="service-title">{service.title}</h3>
                  <p className="service-text">{service.text}</p>
                </article>
              );
            })}
          </div>

          <button
            type="button"
            className="services-arrow services-arrow--right"
            aria-label="Next services"
            onClick={() => scrollByCard(1)}
          >
            <IconArrowRight width={20} height={20} />
          </button>
        </div>

        <div className="services-foot">
          <Link to="/services" className="btn btn-primary">
            View all services
            <IconArrowRight width={17} height={17} />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default Services;
